import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { getRowByLength, imageHeight, imageWidth, rowWidth } from "../../utils/qui-sommes-nous";
import Image from "./image";
import { useFrame, useThree } from "react-three-fiber";
import debounce from 'lodash.debounce';
import TouchTexture from "./touchTexture";
import { EffectComposer, RenderPass, EffectPass } from "postprocessing";
import { WaterEffect } from "./waterEffect";

const getZeroPosition = ({ width, height }) => [
	-width / 2 + imageWidth / 2 * height,
	height / 2 - height * imageHeight / 2,
	0
];

const Scene = ({ mappedProfiles, wrapperRef, setActiveModal, active }) => {
	const { camera, scene, gl, size } = useThree();
	const touchTexture = useMemo(() => new TouchTexture(), []);
	const composer = useMemo(() => new EffectComposer(gl), [gl]);
	const groupRef = useRef(null);
	const [viewSize, setViewSize] = useState({ width: 0, height: 0 });
	const [groupPositionInitialized, setGroupInitialized] = useState(false);

	useEffect(() => {
		if (typeof window !== "undefined") {
			const updateViewSizes = debounce(() => {
				const fovInRadians = (camera.fov * Math.PI) / 180;
				const height = Math.abs(camera.position.z * Math.tan(fovInRadians / 2) * 2);

				setViewSize({ width: height * camera.aspect, height });
			}, 50);

			updateViewSizes();
			window.addEventListener('resize', updateViewSizes);
			return () => void window.removeEventListener('resize', updateViewSizes);
		}
	}, [camera]);

	useEffect(() => {
		if (active && typeof window !== "undefined") {
			const timeout = setTimeout(() => window.dispatchEvent(new Event('resize')), 1000);
			return () => clearTimeout(timeout);
		}
	}, [active]);

	useEffect(() => {
		const container = wrapperRef.current;
		const zeroPositionX = getZeroPosition(viewSize)[0];
		const width = getRowByLength(mappedProfiles.length) * rowWidth * viewSize.height;
		const scrollCoefficient = viewSize.width / wrapperRef.current.getBoundingClientRect().width;
		const maxXRight = zeroPositionX - width + viewSize.width;

		if (groupRef.current.position.x < maxXRight) {
			groupRef.current.position.setX(maxXRight);
		}

		const onWheelListener = e => {
			const currentX = groupRef.current.position.x;
			const newX = -e.deltaY * scrollCoefficient + currentX;
			if (newX < zeroPositionX && newX > zeroPositionX - width + viewSize.width) {
				groupRef.current.position.setX(newX);
				e.preventDefault();
				e.stopPropagation();
			}
		};

		container.addEventListener('wheel', onWheelListener);
		return () => {
			container.removeEventListener('wheel', onWheelListener);
		}
	}, [viewSize, wrapperRef, mappedProfiles, groupRef]);

	useEffect(() => {
		if (!groupPositionInitialized && viewSize.width && viewSize.height) {
			groupRef.current.position.set(...getZeroPosition(viewSize));
			setGroupInitialized(true);
		}
	}, [groupPositionInitialized, setGroupInitialized, viewSize]);

	useEffect(() => void composer.setSize(size.width, size.height), [size, composer]);
	useFrame((state, delta) => {
		touchTexture.update(delta);
	});
	useFrame((state, delta) => active ? composer.render(delta) : null, 1);

	useEffect(() => {
		const renderPass = new RenderPass(scene, camera);
		const waterEffect = new WaterEffect({ texture: touchTexture.texture });
		const waterPass = new EffectPass(camera, waterEffect);
		waterPass.renderToScreen = true;
		renderPass.renderToScreen = false;
		composer.addPass(renderPass);
		composer.addPass(waterPass);

	}, [composer, scene, camera, touchTexture.texture]);

	return (
		<>
			<mesh
				onPointerMove={e => {
					touchTexture.addTouch(e.uv);
				}}
			>
				<planeBufferGeometry
					attach="geometry"
					args={[viewSize.width, viewSize.height, 1, 1]}
				/>
				<meshBasicMaterial attach="material" args={[{ color: 0x000000, opacity: 0 }]}/>
			</mesh>
			<Suspense fallback="">
				<group
					ref={groupRef}
				>
					{mappedProfiles.map(({ image, id }, i) => <Image image={image} key={id} id={id} viewSize={viewSize} index={i}
																													 setActiveModal={setActiveModal}/>)}
				</group>
			</Suspense>
		</>
	)
};

export default Scene;